import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("token");
    if (tokenFromUrl) {
      localStorage.setItem("jjodi_token", tokenFromUrl);
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("jjodi_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) {
      localStorage.setItem("jjodi_token", data.token);
      setUser(data.user);
    }
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("jjodi_token");
    setUser(null);
  };

  useEffect(() => {
    if (user) {
      import("../lib/firebase")
        .then(({ requestFcmToken, setupForegroundNotifications }) => {
          // Register token FIRST, then setup foreground listener after SW is ready
          requestFcmToken(false).then((token) => {
            // Setup foreground listener AFTER service worker is registered
            setupForegroundNotifications();
            if (token) {
              api.post("/notifications/save-token", { token }).catch((err) => {
                console.error("Failed to save FCM token to backend:", err);
              });
            }
          });
        })
        .catch((err) => {
          console.error("FCM: Dynamic import failed:", err);
          api.post("/logs/client", { message: "FCM: Dynamic import failed", error: String(err) }).catch(() => {});
        });
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
